import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Upload, X, Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { getToken } from "../../lib/auth";

const initial = {
  name: "", description: "", category: "Windows", price: 9.99, discount: 0,
  images: [], stock_type: "manual_pool", delivery_type: "instant", active: true, features: [],
};

export default function ProductForm() {
  const { id } = useParams();
  const isNew = id === "new" || !id;
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState(initial);
  const [keys, setKeys] = useState([]);
  const [keysInput, setKeysInput] = useState("");
  const [featureInput, setFeatureInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/products/${id}`).then((r) => setForm({ ...initial, ...r.data }));
      api.get(`/admin/products/${id}/keys`).then((r) => setKeys(r.data.keys));
    }
  }, [id, isNew]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price), discount: parseFloat(form.discount || 0) };
    try {
      if (isNew) {
        const r = await api.post("/admin/products", payload);
        toast.success("Product created");
        nav(`/admin/products/${r.data.id}`);
      } else {
        await api.put(`/admin/products/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setProgress(0);
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append("file", files[i]);
        const res = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${getToken()}` },
          onUploadProgress: (p) => setProgress(Math.round((p.loaded * 100) / (p.total || 1))),
        });
        const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
        setForm((f) => ({ ...f, images: [...f.images, url] }));
      }
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      e.target.value = "";
    }
  };

  const removeImg = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const addFeature = () => {
    if (!featureInput.trim()) return;
    setForm((f) => ({ ...f, features: [...f.features, featureInput.trim()] }));
    setFeatureInput("");
  };
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  const addKeys = async () => {
    const lines = keysInput.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!lines.length) return;
    await api.post(`/admin/products/${id}/keys`, { keys: lines });
    toast.success(`Added ${lines.length} keys`);
    setKeysInput("");
    api.get(`/admin/products/${id}/keys`).then((r) => setKeys(r.data.keys));
  };
  const delKey = async (kid) => {
    await api.delete(`/admin/keys/${kid}`);
    setKeys(keys.filter((k) => k.id !== kid));
  };

  return (
    <div data-testid="product-form-page">
      <Link to="/admin/products" className="font-mono text-xs text-[#A1A1AA] hover:text-[#00FF66] inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" />back to products
      </Link>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading font-bold text-3xl">{isNew ? "New product" : "Edit product"}</h1>
        <button data-testid="save-product-btn" onClick={save} className="btn-primary">save product</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="surface p-6 space-y-4">
            <Field label="name"><input data-testid="form-name" className="input-term" value={form.name} onChange={(e) => upd("name", e.target.value)} /></Field>
            <Field label="description"><textarea className="input-term" rows={4} value={form.description} onChange={(e) => upd("description", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="category"><input className="input-term" value={form.category} onChange={(e) => upd("category", e.target.value)} /></Field>
              <Field label="price"><input data-testid="form-price" type="number" step="0.01" className="input-term" value={form.price} onChange={(e) => upd("price", e.target.value)} /></Field>
              <Field label="discount"><input type="number" step="0.01" className="input-term" value={form.discount} onChange={(e) => upd("discount", e.target.value)} /></Field>
              <Field label="active">
                <select className="input-term" value={form.active ? "1" : "0"} onChange={(e) => upd("active", e.target.value === "1")}>
                  <option value="1">active</option><option value="0">inactive</option>
                </select>
              </Field>
              <Field label="stock type">
                <select className="input-term" value={form.stock_type} onChange={(e) => upd("stock_type", e.target.value)}>
                  <option value="manual_pool">manual key pool</option><option value="unlimited">unlimited</option>
                </select>
              </Field>
              <Field label="delivery">
                <select className="input-term" value={form.delivery_type} onChange={(e) => upd("delivery_type", e.target.value)}>
                  <option value="instant">instant</option><option value="manual">manual</option>
                </select>
              </Field>
            </div>
          </div>

          <div className="surface p-6">
            <h3 className="font-heading font-semibold mb-3">Features</h3>
            <div className="flex gap-2">
              <input className="input-term" value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())} placeholder="e.g. Lifetime license" />
              <button onClick={addFeature} className="btn-ghost"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            <ul className="mt-4 space-y-2">
              {form.features.map((f, i) => (
                <li key={i} className="flex justify-between items-center px-3 py-2 surface text-sm">
                  <span>{f}</span>
                  <button onClick={() => removeFeature(i)} className="text-[#71717A] hover:text-[#FF3B30]"><X className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          </div>

          {!isNew && (
            <div className="surface p-6">
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4 text-[#00FF66]" />License keys ({keys.filter((k) => !k.used).length} unused / {keys.length})</h3>
              <textarea className="input-term" rows={4} placeholder="paste keys, one per line" value={keysInput} onChange={(e) => setKeysInput(e.target.value)} />
              <button onClick={addKeys} className="btn-primary mt-3" data-testid="add-keys-btn"><Plus className="w-3.5 h-3.5" />add keys</button>
              <div className="mt-5 max-h-72 overflow-y-auto border-t border-[#1E293B]">
                {keys.map((k) => (
                  <div key={k.id} className="flex items-center justify-between text-sm py-2 border-b border-[#1E293B]/50">
                    <code className="font-mono text-[12px] break-all">{k.key}</code>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <span className={`chip-mono ${k.used ? "!bg-white/5 !text-[#A1A1AA] !border-white/10" : ""}`}>{k.used ? "used" : "free"}</span>
                      <button onClick={() => delKey(k.id)} className="text-[#71717A] hover:text-[#FF3B30]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="surface p-6">
            <h3 className="font-heading font-semibold mb-3">Images</h3>
            <button onClick={() => fileRef.current?.click()} className="btn-ghost w-full justify-center" data-testid="upload-image-btn">
              <Upload className="w-4 h-4" />upload images
            </button>
            <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} />
            {uploading && (
              <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-[#00FF66] transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2">
              {form.images.map((img, i) => (
                <div key={i} className="relative aspect-square rounded overflow-hidden surface">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-black/70 rounded-full p-1 hover:bg-[#FF3B30]"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">{label}</span>
      {children}
    </label>
  );
}
